<template>
  <div class="container-responsive" style="padding-top: 1rem; padding-bottom: 1rem;">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <v-card class="elevation-3" style="border-radius: 17px;">
    <v-card-title class="text-center" style="background-color: #002060; color: white; border-radius: 17px 17px 0 0;">
        <v-icon left>mdi-domain</v-icon>
        {{ isEditMode ? 'Edit Business Client' : 'Add Business Client' }}
      </v-card-title>

      <v-card-text class="pa-6">
        <v-form ref="form" v-model="valid" lazy-validation>
          <v-row>
            <!-- Company Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Company Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="companyName"
                label="Company Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-domain"
                :rules="[v => !!v || 'Company name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="registrationNumber"
                label="Registration Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-card-account-details"
                :rules="[v => !!v || 'Registration number is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="vatNumber"
                label="VAT Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-receipt"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="businessType"
                :items="businessTypes"
                label="Business Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-briefcase"
                :rules="[v => !!v || 'Business type is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="cifNumber"
                label="CIF Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-identifier"
                readonly
              />
            </v-col>
            

          </v-row>

          <v-row>
            <!-- Contact Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Contact Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="contactPerson"
                label="Contact Person"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-account"
                :rules="[v => !!v || 'Contact person is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="contactEmail"
                label="Contact Email"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Must be a valid email'
                ]"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="contactPhone"
                label="Contact Phone"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-phone"
                :rules="phoneValidationRules"
                @input="formatContactPhone"
                placeholder="074 551 1155"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="website"
                label="Website"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-web"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Address Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Address Information</h3>
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="addressLine1"
                label="Address Line 1"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home"
                :rules="[v => !!v || 'Address is required']"
              />
            </v-col>
            
            <v-col cols="12">
              <v-text-field
                v-model="addressLine2"
                label="Address Line 2"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-home-outline"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="city"
                label="City"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-city"
                :rules="[v => !!v || 'City is required']"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="postalCode"
                label="Postal Code"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-mailbox"
                :rules="[v => !!v || 'Postal code is required']"
              />
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model="province"
                label="Province"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-map-marker"
                :rules="[v => !!v || 'Province is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Banking Details -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Banking Details</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="bankName"
                label="Bank Name"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-bank"
                :rules="[v => !!v || 'Bank name is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="bankAccountNumber"
                label="Bank Account Number"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-credit-card"
                :rules="[v => !!v || 'Account number is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <!-- Membership Information -->
            <v-col cols="12">
              <h3 class="section-title mb-4">Membership Information</h3>
            </v-col>
            
            <v-col cols="12" md="6">
              <v-select
                v-model="membership"
                :items="membershipOptions"
                label="Membership Type"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-star"
                :rules="[v => !!v || 'Membership type is required']"
              />
            </v-col>
            
            <v-col cols="12" md="6">
              <v-text-field
                v-model="monthlyFee"
                label="Monthly Fee (R)"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-currency-usd"
                type="number"
                :rules="[v => !!v || 'Monthly fee is required']"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-textarea
                v-model="notes"
                label="Additional Notes"
                variant="solo"
                rounded
                flat
                hide-details
                prepend-inner-icon="mdi-note-text"
                rows="3"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
               color="#002060"
                dark
                rounded
                size="large"
                :disabled="!valid || loading"
                :loading="loading"
                @click="saveBusiness"
                class="mr-4"
              >
                <v-icon left>{{ isEditMode ? 'mdi-content-save' : 'mdi-domain-plus' }}</v-icon>
                {{ loading ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Business' : 'Save Business') }}
              </v-btn>
              
              <v-btn
                color="grey"
                dark
                rounded
                size="large"
                @click="cancel"
              >
                <v-icon left>mdi-close</v-icon>
                Cancel
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { CIFGenerator } from '../utils/cifGenerator';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';

export default {
  name: 'AddBusiness',
  components: {
    NotificationDialog
  },
  data() {
    return {
      valid: false,
      loading: false,

      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      cifNumber: '',
      
      isEditMode: false,
      clientId: null,
      
      // Company Information
      companyName: '',
      registrationNumber: '',
      vatNumber: '',
      businessType: '',
      businessTypes: [
        'Pty Ltd',
        'Close Corporation',
        'Partnership',
        'Sole Proprietorship',
        'Trust',
        'Non-Profit Organization',
        'Other'
      ],
      
      // Contact Information
      contactPerson: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
      
      // Address Information
      addressLine1: '',
      addressLine2: '',
      city: '',
      postalCode: '',
      province: '',
      
      // Banking Details
      bankName: '',
      bankAccountNumber: '',
      
      // Membership Information
      membership: '',
              membershipOptions: [
          { title: 'Blue Labour', value: 'Blue Labour' },
          { title: 'Silver Labour', value: 'Silver Labour' },
          { title: 'Gold Labour', value: 'Gold Labour' }
        ],
      monthlyFee: '',
      
      // Additional Information
      notes: ''
    };
  },
  computed: {
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  methods: {
    formatContactPhone() {
      this.contactPhone = formatPhoneInput(this.contactPhone);
    },

    async loadClientData() {
      if (!this.clientId) {
        console.error('loadClientData: clientId is missing');
        return;
      }
      
      try {
        this.loading = true;
        const clientDocRef = doc(db, 'clients', this.clientId);
        const clientDoc = await getDoc(clientDocRef);
        
        if (clientDoc.exists()) {
          const clientData = clientDoc.data();
          
          // Map the client data to form fields
          this.cifNumber = clientData.cifNumber || '';
          this.companyName = clientData.companyName || '';
          this.registrationNumber = clientData.registrationNumber || '';
          this.vatNumber = clientData.vatNumber || '';
          this.businessType = clientData.businessType || '';
          this.contactPerson = clientData.contactPerson || '';
          this.contactEmail = clientData.contactEmail || '';
          this.contactPhone = clientData.contactPhone || '';
          this.website = clientData.website || '';
          this.addressLine1 = clientData.addressLine1 || '';
          this.addressLine2 = clientData.addressLine2 || '';
          this.city = clientData.city || '';
          this.postalCode = clientData.postalCode || '';
          this.province = clientData.province || '';
          this.bankName = clientData.bankName || '';
          this.bankAccountNumber = clientData.bankAccountNumber || '';
          this.membership = clientData.membership || '';
          this.monthlyFee = clientData.monthlyFee || '';
          this.notes = clientData.notes || '';
          
        } else {
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Business client not found.';
          this.showNotification = true;
          setTimeout(() => this.$router.back(), 2000);
        }
      } catch (error) {
        console.error('Error loading client data:', error);
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to load client data.';
        this.showNotification = true;
        setTimeout(() => this.$router.back(), 2000);
      } finally {
        this.loading = false;
      }
    },

    async saveBusiness() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        
        try {
          if (this.isEditMode) {
            await this.updateBusiness();
          } else {
            await this.addBusiness();
          }
        } catch (error) {
          console.error('Error saving business:', error);
          this.notificationType = 'error';
          this.notificationTitle = 'Error!';
          this.notificationMessage = 'Failed to save business. Please try again.';
          this.showNotification = true;
        } finally {
          this.loading = false;
        }
      }
    },

    async addBusiness() {
      // Generate CIF number automatically if not provided
      if (!this.cifNumber) {
        try {
          const cifNumber = await CIFGenerator.generateCIF('business');
          this.cifNumber = cifNumber;
        } catch (error) {
          console.error('Error generating CIF, using fallback:', error);
          const cifNumber = CIFGenerator.generateTestCIF('business');
          this.cifNumber = cifNumber;
        }
      }

      const businessData = {
        // CIF Information
        cifNumber: this.cifNumber,
        
        // Company Information
        companyName: this.companyName,
        registrationNumber: this.registrationNumber,
        vatNumber: this.vatNumber,
        businessType: this.businessType,
        
        // Contact Information
        contactPerson: this.contactPerson,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        website: this.website,
        
        // Address Information
        address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}, ${this.province}`,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        city: this.city,
        postalCode: this.postalCode,
        province: this.province,
        
        // Banking Details
        bankName: this.bankName,
        bankAccountNumber: this.bankAccountNumber,
        
        // Membership Information
        membership: this.membership,
        monthlyFee: parseFloat(this.monthlyFee),
        
        // Additional Information
        notes: this.notes,
        
        // Metadata
        clientType: 'business',
        dateAdded: new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        }),
        createdAt: new Date().toISOString(),
        status: 'active'
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'clients'), businessData);
      console.log('Business client added with ID: ', docRef.id);
      
      // Log the activity
      await ActivityLogger.logActivity('Business Client Added', {
        clientId: docRef.id,
        companyName: this.companyName,
        contactPerson: this.contactPerson,
        addedBy: this.$store.getters.user?.displayName || this.$store.getters.user?.email?.split('@')[0] || 'Unknown'
      });
      
      // Show success notification
      this.notificationType = 'success';
      this.notificationTitle = 'Success!';
      this.notificationMessage = `Business client "${this.companyName}" added successfully!`;
      this.showNotification = true;
      
      // Reset form
      this.$refs.form.reset();
      
      // Navigate back to clients list
      setTimeout(() => {
        this.$router.push({ path: '/clients', query: { type: 'companies' } });
      }, 2000);
    },

    async updateBusiness() {
      if (!this.clientId) {
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Client ID is missing. Cannot update business.';
        this.showNotification = true;
        return;
      }

      const businessData = {
        // CIF Information
        cifNumber: this.cifNumber,
        
        // Company Information
        companyName: this.companyName,
        registrationNumber: this.registrationNumber,
        vatNumber: this.vatNumber,
        businessType: this.businessType,
        
        // Contact Information
        contactPerson: this.contactPerson,
        contactEmail: this.contactEmail,
        contactPhone: this.contactPhone,
        website: this.website,
        
        // Address Information
        address: `${this.addressLine1}${this.addressLine2 ? ', ' + this.addressLine2 : ''}, ${this.city}, ${this.postalCode}, ${this.province}`,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        city: this.city,
        postalCode: this.postalCode,
        province: this.province,
        
        // Banking Details
        bankName: this.bankName,
        bankAccountNumber: this.bankAccountNumber,
        
        // Membership Information
        membership: this.membership,
        monthlyFee: parseFloat(this.monthlyFee),
        
        // Additional Information
        notes: this.notes,
        
        // Metadata
        clientType: 'business',
        status: 'active',
        updatedAt: new Date().toISOString()
      };

      // Update in Firestore
      const clientDocRef = doc(db, 'clients', this.clientId);
      await updateDoc(clientDocRef, businessData);
      
      // Log the activity
      await ActivityLogger.logActivity('Business Client Updated', {
        clientId: this.clientId,
        companyName: this.companyName,
        contactPerson: this.contactPerson,
        updatedBy: this.$store.getters.user?.displayName || this.$store.getters.user?.email?.split('@')[0] || 'Unknown'
      });
      
      // Show success notification
      this.notificationType = 'success';
      this.notificationTitle = 'Success!';
      this.notificationMessage = `Business client "${this.companyName}" updated successfully!`;
      this.showNotification = true;
      
      // Navigate back
      setTimeout(() => {
        this.$router.back();
      }, 2000);
    },
    
    cancel() {
      this.$router.push({ path: '/clients', query: { type: 'companies' } });
    },

    resetForm() {
      this.companyName = '';
      this.registrationNumber = '';
      this.vatNumber = '';
      this.businessType = '';
      this.contactPerson = '';
      this.contactEmail = '';
      this.contactPhone = '';
      this.website = '';
      this.addressLine1 = '';
      this.addressLine2 = '';
      this.city = '';
      this.postalCode = '';
      this.province = '';
      this.bankName = '';
      this.bankAccountNumber = '';
      this.membership = '';
      this.monthlyFee = '';
      this.notes = '';
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    async generateCIF() {
      if (!this.cifNumber) {
        const testCifNumber = CIFGenerator.generateTestCIF('business');
        this.cifNumber = testCifNumber;
        
        try {
          const cifNumber = await CIFGenerator.generateCIF('business');
          this.cifNumber = cifNumber;
        } catch (error) {
          console.error('Error generating unique CIF, keeping test CIF:', error);
        }
      }
    }
  },
  
  watch: {
    '$route.query': {
      handler(newQuery) {
        const editParam = newQuery.edit;
        const clientIdParam = newQuery.clientId;
        
        if (editParam === 'true' && clientIdParam) {
          if (!this.isEditMode || this.clientId !== clientIdParam) {
            this.isEditMode = true;
            this.clientId = clientIdParam;
            this.loadClientData();
          }
        } else if (this.isEditMode) {
          this.isEditMode = false;
          this.clientId = null;
          this.resetForm();
          this.generateCIF();
        }
      },
      immediate: true
    }
  },
  
     async created() {
     console.log('AddBusiness component created');
   },

   async mounted() {
     console.log('AddBusiness component mounted');
     console.log('Current cifNumber:', this.cifNumber);
     
     // Generate CIF number automatically when component is mounted (only for new clients)
     if (!this.isEditMode && !this.cifNumber) {
       console.log('Generating CIF number...');
       await this.generateCIF();
     } else {
       console.log('Skipping CIF generation - isEditMode:', this.isEditMode, 'cifNumber exists:', !!this.cifNumber);
     }
   }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #002060;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

:deep(.v-field--variant-solo) {
  background-color: #f8f9fa !important;
}

:deep(.v-field--variant-solo .v-field__overlay) {
  background-color: #f8f9fa !important;
}
</style> 